import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  VideoLibrary as VideoIcon,
  Visibility as ViewIcon,
  People as FollowersIcon,
  ThumbUp as LikeIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import tiktokReportService from '../../services/tiktokReportService';

const MetricCard = ({ icon, label, value, subValue, growth, color, gradient }) => (
  <Card
    sx={{
      borderRadius: 3,
      background: gradient,
      color: 'white',
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-4px)' },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
          {icon}
        </Avatar>
        {growth !== undefined && (
          <Chip
            icon={growth >= 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
            label={`${growth >= 0 ? '+' : ''}${growth}%`}
            size="small"
            sx={{
              bgcolor: growth >= 0 ? 'rgba(255,255,255,0.25)' : 'rgba(239,68,68,0.3)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {label}
      </Typography>
      {subValue && (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {subValue}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const TikTokReport = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    weekCode: '',
    accountName: 'Carnaval Official',
    metrics: {
      totalVideos: 0,
      newVideos: 0,
      totalViews: 0,
      totalFollowers: 0,
      newFollowers: 0,
      totalLikes: 0,
      totalComments: 0,
      totalShares: 0,
    },
    topVideos: [],
    notes: '',
  });
  const [editingId, setEditingId] = useState(null);
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsResult, reportsResult, currentResult] = await Promise.all([
        tiktokReportService.getStats(),
        tiktokReportService.getAll({ limit: 10 }),
        tiktokReportService.getCurrentWeek(),
      ]);

      if (statsResult.success) setStats(statsResult.data);
      if (reportsResult.success) setReports(reportsResult.data);
      if (currentResult.success) setCurrentReport(currentResult.data);
    } catch (error) {
      showSnackbar('Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenCreate = () => {
    if (currentReport) {
      setFormData({
        weekCode: currentReport.weekCode,
        accountName: currentReport.accountName || 'Carnaval Official',
        metrics: { ...currentReport.metrics },
        topVideos: currentReport.topVideos || [],
        notes: currentReport.notes || '',
      });
      setEditingId(currentReport._id);
    } else {
      const weekCode = tiktokReportService.getWeekCode();
      setFormData({
        weekCode,
        accountName: 'Carnaval Official',
        metrics: {
          totalVideos: 0,
          newVideos: 0,
          totalViews: 0,
          totalFollowers: 0,
          newFollowers: 0,
          totalLikes: 0,
          totalComments: 0,
          totalShares: 0,
        },
        topVideos: [],
        notes: '',
      });
      setEditingId(null);
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await tiktokReportService.update(editingId, formData);
        showSnackbar('Cập nhật báo cáo thành công!');
      } else {
        await tiktokReportService.create(formData);
        showSnackbar('Tạo báo cáo thành công!');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Lỗi khi lưu báo cáo', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa báo cáo này?')) return;
    try {
      await tiktokReportService.delete(id);
      showSnackbar('Đã xóa báo cáo!');
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi xóa', 'error');
    }
  };

  const updateMetric = (key, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [key]: parseInt(value) || 0,
      },
    }));
  };

  const formatNum = tiktokReportService.formatNumber;

  if (loading) {
    return <LinearProgress />;
  }

  const latest = stats?.latest || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            📊 Báo cáo TikTok
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Theo dõi chỉ số & hiệu suất content TikTok hàng tuần
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchData}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={currentReport?.isNew ? <AddIcon /> : <EditIcon />}
            onClick={handleOpenCreate}
            sx={{
background: 'linear-gradient(135deg, #fe2c55 0%, #25f4ee 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #e91e4a 0%, #1de0db 100%)' },
            }}
          >
            {currentReport?.isNew ? 'Tạo báo cáo tuần này' : 'Cập nhật báo cáo'}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<FollowersIcon />}
            label="Followers"
            value={formatNum(latest.totalFollowers)}
            subValue={`+${formatNum(latest.newFollowers)} tuần này`}
            growth={stats?.growth?.followers}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<ViewIcon />}
            label="Views tuần"
            value={formatNum(latest.totalViews)}
            growth={stats?.growth?.views}
            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<VideoIcon />}
            label="Videos mới"
            value={latest.newVideos || 0}
            subValue={`${formatNum(latest.avgViewsPerVideo)} views/video`}
            gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<LikeIcon />}
            label="Engagement Rate"
            value={`${latest.engagementRate || 0}%`}
            subValue={`${formatNum(latest.totalLikes)} likes`}
            gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
          />
        </Grid>
      </Grid>

      {/* Engagement Breakdown */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Tương tác tuần này
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <LikeIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatNum(latest.totalLikes)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Likes</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <CommentIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatNum(latest.totalComments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Comments</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <ShareIcon sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatNum(latest.totalShares)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Shares</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Reports History */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📋 Lịch sử báo cáo
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Tuần</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Videos</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Views</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Followers</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Engagement</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Người báo cáo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon sx={{ fontSize: 18, color: '#64748b' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {report.weekCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(report.weekStart), 'dd/MM', { locale: vi })} - {format(new Date(report.weekEnd), 'dd/MM', { locale: vi })}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={`+${report.metrics.newVideos}`} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{formatNum(report.metrics.totalViews)}</TableCell>
                  <TableCell>
                    {formatNum(report.metrics.totalFollowers)}
                    <Typography variant="caption" sx={{ ml: 0.5, color: '#10b981' }}>
                      +{formatNum(report.metrics.newFollowers)}
                    </Typography>
                  </TableCell>
                  <TableCell>{report.metrics.engagementRate}%</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={report.reportedBy?.avatar}
                        sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                      >
                        {report.reportedBy?.fullName?.[0]}
                      </Avatar>
                      <Typography variant="body2">{report.reportedBy?.fullName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Xóa">
                      <IconButton size="small" color="error" onClick={() => handleDelete(report._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">Chưa có báo cáo nào</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoIcon sx={{ color: '#fe2c55' }} />
            {editingId ? 'Cập nhật báo cáo TikTok' : 'Tạo báo cáo TikTok mới'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tuần"
                value={formData.weekCode}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tên Account"
                value={formData.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="📊 Chỉ số" size="small" />
              </Divider>
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Tổng Videos"
                type="number"
                value={formData.metrics.totalVideos}
                onChange={(e) => updateMetric('totalVideos', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Videos mới tuần này"
                type="number"
                value={formData.metrics.newVideos}
                onChange={(e) => updateMetric('newVideos', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Tổng Followers"
                type="number"
                value={formData.metrics.totalFollowers}
                onChange={(e) => updateMetric('totalFollowers', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Followers mới"
                type="number"
                value={formData.metrics.newFollowers}
                onChange={(e) => updateMetric('newFollowers', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="👁️ Views & Engagement" size="small" />
              </Divider>
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Total Views tuần"
                type="number"
                value={formData.metrics.totalViews}
                onChange={(e) => updateMetric('totalViews', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Likes"
                type="number"
                value={formData.metrics.totalLikes}
                onChange={(e) => updateMetric('totalLikes', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Comments"
                type="number"
                value={formData.metrics.totalComments}
                onChange={(e) => updateMetric('totalComments', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Shares"
                type="number"
                value={formData.metrics.totalShares}
                onChange={(e) => updateMetric('totalShares', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ghi chú"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setModalOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              background: 'linear-gradient(135deg, #fe2c55 0%, #25f4ee 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #e91e4a 0%, #1de0db 100%)' },
            }}
          >
            {editingId ? 'Cập nhật' : 'Tạo báo cáo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TikTokReport;
